import React, { useState, useEffect, useRef } from 'react';
import { Search, X } from 'lucide-react';
import ProductDetail from './ProductDetail';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const SearchBar = ({ textColor = 'text-[#001B2A]' }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState(null);
  const panelRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && products.length === 0) {
      fetch(`${API_URL}/api/products`)
        .then((r) => r.json())
        .then((data) => setProducts(Array.isArray(data) ? data : []))
        .catch(() => setProducts([]));
    }
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open, products.length]);

  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const q = query.trim().toLowerCase();
  const results = q
    ? products
        .filter((p) =>
          [p.name, p.description, p.material, p.category, ...(p.colors || [])]
            .filter(Boolean)
            .some((f) => f.toLowerCase().includes(q))
        )
        .slice(0, 8)
    : [];

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setOpen(!open)}
        data-testid="navbar-search-button"
        className={`p-2 ${textColor} hover:text-[#7B1E2B] transition-colors`}
        aria-label="Search products"
      >
        <Search className="h-5 w-5 md:h-6 md:w-6" />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-[calc(100vw-2rem)] max-w-sm bg-white rounded-lg shadow-2xl border border-gray-100 overflow-hidden" data-testid="search-panel">
          <div className="flex items-center gap-2 p-3 border-b border-gray-100">
            <Search className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <input
              ref={inputRef}
              data-testid="search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search bow ties, colors, styles..."
              className="flex-1 text-sm text-[#001B2A] focus:outline-none"
            />
            {query && (
              <button onClick={() => setQuery('')} className="p-1 text-gray-400 hover:text-gray-600" data-testid="search-clear">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <div className="max-h-80 overflow-y-auto">
            {q && results.length === 0 && (
              <p className="p-4 text-sm text-gray-500 text-center" data-testid="search-no-results">
                No products found for "{query}"
              </p>
            )}
            {results.map((p) => (
              <button
                key={p.id}
                data-testid={`search-result-${p.id}`}
                onClick={() => { setSelected(p); setOpen(false); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 transition-colors text-left"
              >
                <img src={p.image} alt={p.name} className="w-10 h-10 rounded object-cover flex-shrink-0" />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-semibold text-[#001B2A] truncate">{p.name}</span>
                  <span className="block text-xs text-gray-500 capitalize">{(p.category || '').replace(/-/g, ' ')}</span>
                </span>
                <span className="text-sm font-bold text-[#7B1E2B] flex-shrink-0">${p.price.toFixed(2)}</span>
              </button>
            ))}
            {!q && (
              <p className="p-4 text-xs text-gray-400 text-center">Start typing to search {products.length || ''} products</p>
            )}
          </div>
        </div>
      )}

      {selected && <ProductDetail product={selected} onClose={() => setSelected(null)} />}
    </div>
  );
};
